// ============================================
// SIDEBARFILTROS.JS — Sidebar contextual desktop con hermanas + marcas
// ============================================
// Visible solo cuando hay categoría activa en La Tienda (escritorio).

import React, { useEffect, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  COLOR_PRINCIPAL,
} from './helpersClientes';
import { tokens } from '../styles/tokens';
import { authHeaders } from '../utils/storage';

// Convierte "syscom_22" o "22" → 22
function rawId(id) {
  const s = String(id || '');
  return Number(s.replace(/^syscom_/, '')) || null;
}

export default function SidebarFiltros({
  categoriaActiva, marcasSeleccionadas = [], onCambiarMarcas,
  onCambiarCategoria, onLimpiar,
  orden = 'precio_asc', onCambiarOrden,
  // CCTV FASE 2: facetas + filtros tecnicos
  facetas = null,
  cctvCamaraMp = [], onCambiarCamaraMp,
  cctvCanales = [], onCambiarCanales,
  cctvMpMin = null, onCambiarMpMin,
}) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    const id = rawId(categoriaActiva);
    if (!id) { setDatos(null); return; }
    let activo = true;
    setCargando(true);
    fetch(`${API_BASE_CLI}/api/tienda/categoria/${id}/sidebar`, { headers: authHeaders() })
      .then(x => x.json())
      .then(r => { if (activo && r.ok) setDatos(r); })
      .catch(() => {})
      .finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
  }, [categoriaActiva]);

  if (!categoriaActiva) return null;

  const id = rawId(categoriaActiva);
  const marcas = datos?.marcas_disponibles || [];
  const hermanas = datos?.hermanas || [];

  return (
    <div style={{
      width: 240, flexShrink: 0,
      background: '#fff',
      borderRight: `1px solid ${COLOR_BORDE}`,
      padding: 14,
      overflowY: 'auto',
      position: 'sticky', top: 0,
      maxHeight: 'calc(100vh - 0px)',
      fontFamily: PP, color: COLOR_GRIS,
    }}>
      <button
        onClick={onLimpiar}
        style={{
          background: 'none', border: 'none',
          color: COLOR_PRINCIPAL, fontWeight: 600, fontSize: 14,
          padding: '6px 0', cursor: 'pointer', marginBottom: 12,
          fontFamily: PP,
        }}
      >← Volver a categorías</button>

      {cargando && (
        <div style={{ padding: 16, color: COLOR_GRIS_CLARO, fontSize: 12 }}>
          Cargando filtros…
        </div>
      )}

      {/* Ordenar por */}
      <div style={{ marginBottom: 22 }}>
        <h3 style={titulo}>Ordenar por</h3>
        {[
          { v: 'precio_asc',  l: 'Precio: menor a mayor' },
          { v: 'precio_desc', l: 'Precio: mayor a menor' },
          { v: 'stock_desc',  l: 'Más stock disponible' },
        ].map(opt => {
          const sel = orden === opt.v;
          return (
            <label
              key={opt.v}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', cursor: 'pointer',
                borderRadius: 6,
                fontSize: 13,
                background: sel ? tokens.primarySoft : 'transparent',
                color: sel ? COLOR_PRINCIPAL : COLOR_OSCURO,
                fontWeight: sel ? 600 : 500,
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = tokens.surfaceSubtle; }}
              onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="radio"
                name="orden"
                value={opt.v}
                checked={sel}
                onChange={(e) => onCambiarOrden && onCambiarOrden(e.target.value)}
                style={{ accentColor: COLOR_PRINCIPAL, flexShrink: 0 }}
              />
              <span>{opt.l}</span>
            </label>
          );
        })}
      </div>

      {/* Filtrar por (CCTV: megapixeles / canales / soporta hasta) */}
      <FiltrosCctv
        facetas={facetas}
        camaraMp={cctvCamaraMp} onCamaraMp={onCambiarCamaraMp}
        canales={cctvCanales} onCanales={onCambiarCanales}
        mpMin={cctvMpMin} onMpMin={onCambiarMpMin}
      />

      {/* Hermanas */}
      {hermanas.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <h3 style={titulo}>Categorías</h3>
          {hermanas.map(c => {
            const activa = c.id === id;
            return (
              <div
                key={c.id}
                onClick={() => !activa && onCambiarCategoria && onCambiarCategoria(`syscom_${c.id}`)}
                style={{
                  padding: '7px 10px', borderRadius: 6,
                  fontSize: 13, cursor: activa ? 'default' : 'pointer',
                  background: activa ? tokens.primarySoft : 'transparent',
                  color: activa ? COLOR_PRINCIPAL : COLOR_OSCURO,
                  fontWeight: activa ? 600 : 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}
                onMouseEnter={(e) => { if (!activa) e.currentTarget.style.background = tokens.surfaceSubtle; }}
                onMouseLeave={(e) => { if (!activa) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                }}>
                  {activa ? '★ ' : ''}{c.nombre}
                </span>
                <span style={{ color: COLOR_GRIS_CLARO, fontSize: 11, fontWeight: 500 }}>
                  ({c.count})
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Marcas */}
      {marcas.length > 0 && (
        <div>
          <h3 style={titulo}>Marcas</h3>
          {marcas.map(m => {
            const sel = marcasSeleccionadas.includes(m.nombre);
            return (
              <label
                key={m.nombre}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', cursor: 'pointer',
                  fontSize: 13, color: COLOR_GRIS, borderRadius: 6,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = tokens.surfaceSubtle; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={(e) => {
                    const set = new Set(marcasSeleccionadas);
                    if (e.target.checked) set.add(m.nombre);
                    else set.delete(m.nombre);
                    onCambiarMarcas && onCambiarMarcas([...set]);
                  }}
                  style={{ flexShrink: 0 }}
                />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.nombre}
                </span>
                <span style={{ color: COLOR_GRIS_CLARO, fontSize: 11, fontWeight: 500 }}>
                  ({m.count})
                </span>
              </label>
            );
          })}
          {marcasSeleccionadas.length > 0 && (
            <button
              onClick={() => onCambiarMarcas && onCambiarMarcas([])}
              style={{
                marginTop: 8, fontSize: 12, color: '#e05c5c',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 8px', fontFamily: PP,
              }}
            >× Limpiar marcas ({marcasSeleccionadas.length})</button>
          )}
        </div>
      )}

      {!cargando && !hermanas.length && !marcas.length && (
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: 8 }}>
          Sin filtros adicionales para esta categoría.
        </div>
      )}
    </div>
  );
}

// ============================================
// FiltrosCctv — seccion "Filtrar por" segun el tipo de lo que se ve
// (camaras -> Megapixeles; grabadores -> Canales + Soporta hasta N MP).
// Se pinta solo si las facetas traen valores. Oculta valores ruido (n < 3),
// salvo que ya esten seleccionados.
// ============================================
function fmtMP(v) {
  const n = Number(v);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} MP`;
}

function FiltrosCctv({ facetas, camaraMp, onCamaraMp, canales, onCanales, mpMin, onMpMin }) {
  const f = facetas || {};
  const MIN_N = 3;
  const camOpts = (f.camara_mp || []).filter(o => o.n >= MIN_N || camaraMp.includes(o.valor));
  const canOpts = (f.grabador_canales || []).filter(o => o.n >= MIN_N || canales.includes(o.valor));
  const mpOpts = (f.grabador_mp || []).filter(o => o.n >= MIN_N || mpMin === o.valor);

  const esCamara = f.tipo === 'camara' && camOpts.length > 0;
  const esGrabador = f.tipo === 'grabador' && (canOpts.length > 0 || mpOpts.length > 0);
  if (!esCamara && !esGrabador) return null;

  const hayFiltro = camaraMp.length > 0 || canales.length > 0 || mpMin != null;

  const toggle = (arr, val, setter) => {
    const s = new Set(arr);
    if (s.has(val)) s.delete(val); else s.add(val);
    setter([...s].sort((a, b) => a - b));
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <h3 style={titulo}>Filtrar por</h3>

      {esCamara && (
        <>
          <div style={subtitulo}>Megapíxeles</div>
          {camOpts.map(o => {
            const sel = camaraMp.includes(o.valor);
            return (
              <label key={o.valor} style={filaCheck(sel)}>
                <input type="checkbox" checked={sel}
                  onChange={() => toggle(camaraMp, o.valor, onCamaraMp)}
                  style={{ flexShrink: 0, accentColor: COLOR_PRINCIPAL }} />
                <span style={{ flex: 1 }}>{fmtMP(o.valor)}</span>
                <span style={conteo}>({o.n})</span>
              </label>
            );
          })}
        </>
      )}

      {esGrabador && canOpts.length > 0 && (
        <>
          <div style={subtitulo}>Canales</div>
          {canOpts.map(o => {
            const sel = canales.includes(o.valor);
            return (
              <label key={o.valor} style={filaCheck(sel)}>
                <input type="checkbox" checked={sel}
                  onChange={() => toggle(canales, o.valor, onCanales)}
                  style={{ flexShrink: 0, accentColor: COLOR_PRINCIPAL }} />
                <span style={{ flex: 1 }}>{o.valor} canales</span>
                <span style={conteo}>({o.n})</span>
              </label>
            );
          })}
        </>
      )}

      {esGrabador && mpOpts.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={subtitulo}>Soporta hasta</div>
          {mpOpts.map(o => {
            const sel = mpMin === o.valor;
            return (
              <label key={o.valor} style={filaCheck(sel)}
                onClick={() => onMpMin(sel ? null : o.valor)}>
                <input type="radio" readOnly checked={sel}
                  style={{ flexShrink: 0, accentColor: COLOR_PRINCIPAL }} />
                <span style={{ flex: 1 }}>{fmtMP(o.valor)} o más</span>
                <span style={conteo}>({o.n})</span>
              </label>
            );
          })}
        </div>
      )}

      {hayFiltro && (
        <button
          onClick={() => { onCamaraMp([]); onCanales([]); onMpMin(null); }}
          style={{
            marginTop: 8, fontSize: 12, color: '#e05c5c',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px 8px', fontFamily: PP,
          }}
        >× Limpiar filtros</button>
      )}
    </div>
  );
}

const titulo = {
  fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO,
  textTransform: 'uppercase', letterSpacing: 0.5,
  margin: '0 0 8px',
};

const subtitulo = {
  fontSize: 12, fontWeight: 600, color: COLOR_OSCURO,
  margin: '6px 0 4px',
};

const conteo = {
  color: COLOR_GRIS_CLARO, fontSize: 11, fontWeight: 500,
};

const filaCheck = (sel) => ({
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px', cursor: 'pointer', borderRadius: 6,
  fontSize: 13, color: sel ? COLOR_PRINCIPAL : COLOR_GRIS,
  fontWeight: sel ? 600 : 500,
});
